/**
 * Persona state — active persona for the demo session.
 * Persists to localStorage so the switcher survives page refreshes.
 *
 * _hasHydrated: false until onRehydrateStorage fires. Pages that gate
 * persona-locked redirects must wait for this flag before redirecting,
 * otherwise the default P1 initial state causes a hydration-race redirect.
 */
"use client";

import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  type PersonaId,
  type Persona,
  getPersona,
  phase1Personas,
} from "@petci/fixtures";

interface PersonaState {
  activeId: PersonaId;
  _hasHydrated: boolean;
  setPersona: (id: PersonaId) => void;
  setHasHydrated: (b: boolean) => void;
}

export const usePersonaStore = create<PersonaState>()(
  persist(
    (set) => ({
      activeId: "P1",
      _hasHydrated: false,
      setPersona: (id: PersonaId) => set({ activeId: id }),
      setHasHydrated: (b: boolean) => set({ _hasHydrated: b }),
    }),
    {
      name: "petci_demo_persona",
      partialize: (s) => ({ activeId: s.activeId }),
      onRehydrateStorage: () => (state) => {
        if (state) state.setHasHydrated(true);
      },
    },
  ),
);

/**
 * Returns the currently active Persona object.
 * Selects the primitive `activeId` so Zustand's Object.is comparison detects
 * every persona switch — avoids the stale-reference bug where `getPersona`
 * returns the same cached object on P1→P3→P1 and the UI doesn't re-render.
 */
export function useActivePersona(): Persona {
  const activeId = usePersonaStore((s) => s.activeId);
  return getPersona(activeId);
}

/** Returns true once localStorage rehydration has completed. */
export function usePersonaHasHydrated(): boolean {
  return usePersonaStore((s) => s._hasHydrated);
}

/** Returns the Phase-1 visible personas for the switcher (P1–P4). */
export function usePhase1Personas(): readonly Persona[] {
  return phase1Personas;
}

/** Brand-colored avatar classes keyed by PersonaId — single source for all switchers. */
export const PERSONA_AVATAR_COLORS: Record<string, string> = {
  P1: "bg-brand-primary text-white",
  P2: "bg-brand-secondary text-white",
  P3: "bg-brand-secondary-mid text-white",
  P4: "bg-neutral-700 text-white",
};

/** i18n-keyed customer type labels keyed by customerType enum value. */
export const CUSTOMER_TYPE_KEYS: Record<string, string> = {
  RESIDENTIAL: "toolbar.personaTypeResidential",
  COMMERCIAL_SMALL: "toolbar.personaTypeCommercial",
  COMMERCIAL_LARGE: "toolbar.personaTypeHotel",
  PROPERTY_MANAGER: "toolbar.personaTypeProperty",
};
